import { Button } from '@syncanix/ui';

import { useT } from '@/core/providers/I18nProvider';

import type { ReactElement } from 'react';

export interface AppErrorFallbackProps {
  /** Clears the boundary's captured error and retries rendering the app. */
  onRetry: () => void;
}

/**
 * Full-viewport, localized, recoverable fallback for the global
 * `ErrorBoundary`. Announced via `role="alert"`, themed (renders inside
 * `ThemeProvider`), and offers three recoveries.
 *
 * The third one is load-bearing, not decoration. Retry and Reload both re-render
 * the SAME children at the SAME URL, so they only help when the failure was
 * transient. For a **deterministic** render error — one that is a pure function
 * of the URL, e.g. a malformed `?range=custom&since=…` bound — both re-throw
 * immediately, and because this boundary wraps the whole app body (AppShell
 * included) there is no nav left to click: the operator is stranded with no
 * in-app escape. "Go to dashboard home" is a **hard navigation** (`<a href="/">`,
 * not a router `<Link>`) precisely because it changes the input and remounts
 * everything — it works even when the router or a provider is the broken thing.
 */
export function AppErrorFallback({ onRetry }: AppErrorFallbackProps): ReactElement {
  const { t } = useT();
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-8 text-foreground">
      <section role="alert" className="max-w-md space-y-6 text-center">
        <h1 className="text-h1 text-foreground">{t('common.error')}</h1>
        <p className="text-sm text-muted-foreground">{t('error.boundary.description')}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={() => {
              onRetry();
            }}
          >
            {t('common.retry')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              window.location.reload();
            }}
          >
            {t('error.boundary.reload')}
          </Button>
          <Button asChild variant="secondary">
            <a href="/">{t('error.boundary.home')}</a>
          </Button>
        </div>
      </section>
    </main>
  );
}
